import { useRef } from "react";

export interface ChipOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface ChipGroupProps {
  value: string;
  options: ChipOption[];
  onChange: (value: string) => void;
  label: string;
  // The dark hero card needs light chips.
  tone?: "plain" | "inverse";
}

// A short, closed set (currency, account type, kind) reads better as visible
// chips than as a collapsed menu: every choice is on screen and one tap away.
// Arrow keys move within the group, which owns a single tab stop.
export function ChipGroup({ value, options, onChange, label, tone = "plain" }: ChipGroupProps) {
  const holder = useRef<HTMLDivElement>(null);

  function move(from: number, direction: 1 | -1) {
    for (let offset = 1; offset <= options.length; offset += 1) {
      const next = (from + direction * offset + options.length * offset) % options.length;
      if (!options[next]?.disabled) {
        onChange(options[next].value);
        holder.current?.querySelectorAll<HTMLButtonElement>("button")[next]?.focus();
        return;
      }
    }
  }

  const current = Math.max(0, options.findIndex((option) => option.value === value));
  return (
    <div className={`chip-group chip-group-${tone}`} role="radiogroup" aria-label={label} ref={holder}>
      {options.map((option, index) => (
        <button
          key={option.value} type="button" className="chip" role="radio"
          aria-checked={option.value === value} disabled={option.disabled}
          tabIndex={index === current ? 0 : -1}
          onClick={() => onChange(option.value)}
          onKeyDown={(event) => {
            if (event.key === "ArrowRight" || event.key === "ArrowDown") { event.preventDefault(); move(index, 1); }
            if (event.key === "ArrowLeft" || event.key === "ArrowUp") { event.preventDefault(); move(index, -1); }
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
